import { CheckCircle } from 'lucide-react';
import { DropdownWithImagePreview } from '../../forms/DropdownWithImagePreview';

interface FormFieldRendererProps {
  field: any;
  value: any;
  hasError: boolean;
  disabled: boolean;
  onChange: (value: any) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

export const FormFieldRenderer = ({
  field,
  value,
  hasError,
  disabled,
  onChange,
  onFocus,
  onBlur
}: FormFieldRendererProps) => {
  
  if (!field.type) {
    return (
      <div className="w-full bg-elevated/30 border border-subtle/50 rounded-lg px-4 py-3 text-th-secondary text-center opacity-50">
        Select a field type to preview
      </div>
    );
  }

  const placeholder = field.placeholder || `Enter ${field.label.toLowerCase()}`;
  const borderColor = hasError ? 'border-status-error' : 'border-subtle focus:border-brand';

  switch (field.type) {
    case 'text':
    case 'email':
    case 'password':
    case 'url':
    case 'tel':
      return (
        <input
          type={field.type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          className={`w-full bg-elevated border rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:outline-none transition-all ${borderColor}`}
          required={field.required}
          disabled={disabled}
        />
      );

    case 'textarea':
      return (
        <textarea
          placeholder={placeholder}
          rows={field.rows || 4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          className={`w-full bg-elevated border rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary resize-none focus:outline-none transition-all ${borderColor}`}
          required={field.required}
          disabled={disabled}
        />
      );

    case 'number':
      return (
        <input
          type="number"
          placeholder={placeholder}
          min={field.min}
          max={field.max}
          step={field.step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || '')}
          onFocus={onFocus}
          onBlur={onBlur}
          className={`w-full bg-elevated border rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:outline-none transition-all ${borderColor}`}
          required={field.required}
          disabled={disabled}
        />
      );

    case 'select':
      return (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          className={`w-full bg-elevated border rounded-lg px-4 py-3 text-th-primary focus:outline-none transition-all ${borderColor}`}
          required={field.required}
          disabled={disabled}
        >
          <option value="">Select an option...</option>
          {(field.options || []).map((option: any, index: number) => (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );

    case 'range':
      return (
        <div className="space-y-2">
          <input
            type="range"
            min={field.min || 0}
            max={field.max || 100}
            step={field.step || 1}
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="w-full accent-brand"
            required={field.required}
            disabled={disabled}
          />
          <div className="flex justify-between text-xs text-th-secondary">
            <span>{field.min || 0}</span>
            <span className="text-brand font-medium">{value}</span>
            <span>{field.max || 100}</span>
          </div>
        </div>
      );

    case 'boolean':
      return (
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={value}
            onChange={(e) => onChange(e.target.checked)}
            className="w-4 h-4 text-brand bg-elevated border-subtle rounded focus:ring-brand focus:ring-2"
            disabled={disabled}
          />
          <span className="text-th-primary">{field.label}</span>
        </label>
      );

    case 'file':
      return (
        <div className="border-2 border-dashed border-subtle rounded-lg p-6 text-center bg-elevated/30 hover:border-brand/50 transition-colors cursor-pointer">
          <div className="text-th-secondary">
            <input
              type="file"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                onChange(field.multiple ? files : files[0]);
              }}
              className="hidden"
              id={`file-${field.id}`}
              multiple={field.multiple}
              accept={field.accept}
              disabled={disabled}
            />
            <label htmlFor={`file-${field.id}`} className="cursor-pointer">
              {value ? (
                <div className="text-status-success">
                  <CheckCircle className="w-6 h-6 mx-auto mb-2" />
                  <p className="text-sm font-medium">
                    {field.multiple ? `${Array.isArray(value) ? value.length : 1} file(s) selected` : 'File selected'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="w-8 h-8 mx-auto mb-2 text-th-secondary">
                    📁
                  </div>
                  <p className="text-sm">
                    {field.placeholder || 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-xs mt-1">
                    {field.accept ? `Accepted: ${field.accept}` : 'Supported formats: JPG, PNG, MP4, etc.'}
                    {field.multiple && ' • Multiple files allowed'}
                  </p>
                </>
              )}
            </label>
          </div>
        </div>
      );

    case 'date':
      return (
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full bg-elevated border rounded-lg px-4 py-3 text-th-primary focus:outline-none transition-all ${borderColor}`}
          required={field.required}
          disabled={disabled}
        />
      );

    case 'time':
      return (
        <input
          type="time"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full bg-elevated border rounded-lg px-4 py-3 text-th-primary focus:outline-none transition-all ${borderColor}`}
          required={field.required}
          disabled={disabled}
        />
      );

    case 'dropdown_with_image':
      return (
        <DropdownWithImagePreview
          options={field.imageOptions || []}
          value={value}
          onChange={onChange}
          placeholder={field.placeholder}
          allowCustomValue={field.allowCustomValue || false}
         customValueLabel={field.customValueLabel || "Enter Custom Value"}
         customButtonLabel={field.customButtonLabel || "Use Custom Value"}
         customInputPlaceholder={field.customInputPlaceholder || "Type your custom value..."}
          disabled={disabled}
        />
      );

    case 'prompt_builder':
      return (
        <div className="bg-brand/10 border border-brand/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <svg className="w-5 h-5 text-brand" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <span className="text-brand font-medium">Prompt Builder Preview</span>
          </div>
          
          {field.promptBuilderConfig?.template ? (
            <div className="space-y-3">
              <div className="text-th-secondary text-sm">
                <span className="font-medium">Template:</span>
                <code className="ml-2 text-brand font-mono text-xs">
                  {field.promptBuilderConfig.template}
                </code>
              </div>
              <div className="text-th-secondary text-sm">
                <span className="font-medium">Sub-fields:</span>
                <span className="ml-2 text-accent">
                  {field.promptBuilderConfig.sub_fields?.length || 0} field{field.promptBuilderConfig.sub_fields?.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="bg-surface border border-subtle rounded p-3">
                <p className="text-th-secondary text-xs">Example output:</p>
                <p className="text-th-primary text-sm mt-1 font-medium">
                  {field.promptBuilderConfig.template.replace(/\{([^}]+)\}/g, (_: string, placeholder: string) => {
                    const subField = field.promptBuilderConfig?.sub_fields.find((f: any) => f.variable_name === placeholder);
                    return subField?.defaultValue || `[${subField?.label || placeholder}]`;
                  })}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <svg className="w-8 h-8 text-th-secondary mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              <p className="text-th-secondary text-sm">Prompt builder not configured</p>
            </div>
          )}
        </div>
      );

    case 'color':
      return (
        <div className="flex items-center space-x-3">
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-12 h-12 bg-elevated border border-subtle rounded-lg cursor-pointer"
            disabled={disabled}
          />
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`flex-1 bg-elevated border rounded-lg px-4 py-3 text-th-primary focus:outline-none transition-all ${borderColor}`}
            placeholder="#6b6b70"
            pattern="^#[0-9A-Fa-f]{6}$"
            required={field.required}
            disabled={disabled}
          />
        </div>
      );

    default:
      return (
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full bg-elevated border rounded-lg px-4 py-3 text-th-primary placeholder-th-tertiary focus:outline-none transition-all ${borderColor}`}
          required={field.required}
          disabled={disabled}
        />
      );
  }
};